<template>
	<div class="search-drugs">
		<v-autocomplete
			solo
			v-model="drug"
			:items="drugOptions"
			:loading="loadingDrugs"
			:search-input.sync="search"
			hide-selected
			item-text="listName"
			item-value="id"
			:placeholder="$gettext('Search Drug...')"
			return-object
			outlined
			no-filter
			:no-data-text="$gettext('No results found')"
			@input="clickDrug"
			prepend-inner-icon="search"
			hide-details
			:disabled="disabled"
			:style="$store.getters.tourMode ? {'position': 'relative', 'z-index': 400, 'background': '#FFFFFF'} : ''"
		></v-autocomplete>
		<v-row v-if="context == 'appointment'" no-gutters class="mt-2">
			<v-col cols="12">
				<v-btn depressed small class="float-right" @click="importDrugs" :disabled="disabled">
					<translate>Import</translate>
					<v-icon small>mdi-file-import-outline</v-icon>
				</v-btn>
				<v-dialog v-model="showAppointmentsToImport" max-width="40%" scrollable>
					<v-card>
						<v-card-title class="title primary--text mb-2 secondary">
							<translate>Import Drugs</translate>
						</v-card-title>
						<v-card-text>
							<p
								v-if="appointmentsToImport.length == 0"
								class="font-weight-thin ml-2"
								v-translate
							>No appointments.</p>
							<v-list v-else flat>
								<v-list-item
									v-for="appointment in appointmentsToImport"
									:key="appointment.id"
									style="border: 1px solid #e0e0e0; border-radius: 5px"
									@click="importAppointmentDrugs(appointment.id)"
								>
									<v-list-item-icon class="mr-3">
										<v-icon color="primary">assignment</v-icon>
									</v-list-item-icon>
									<v-list-item-content>
										<v-list-item-title>
											<strong>{{appointment.start_date}}</strong>
										</v-list-item-title>
										<v-list-item-subtitle>{{appointment.speciality.name}}</v-list-item-subtitle>
									</v-list-item-content>
								</v-list-item>
							</v-list>
						</v-card-text>
						<v-divider></v-divider>
						<v-card-actions>
							<v-spacer></v-spacer>
							<v-btn color="primary darken-1" text @click="showAppointmentsToImport = false">
								<translate>Close</translate>
							</v-btn>
						</v-card-actions>
					</v-card>
				</v-dialog>
			</v-col>
		</v-row>
		<DrugList
			:list="selectedDrugs"
			:context="context"
			@remove-drug="removeDrug"
			@edit-drug="editDrug"
			class="mt-3 px-2"
			:drugInteractions="drugInteractions"
			:disabled="disabled"
		/>
		<v-dialog v-model="showDrugInteractionOptions" width="50%" scrollable>
			<v-card>
				<v-card-title class="title primary--text mb-2 secondary">
					<translate>Drug Interaction</translate>
					<span class="ml-1" v-if="drug && drug.id != -1">- {{drug.name}}</span>
				</v-card-title>
				<v-card-text>
					<v-row no-gutters class="mt-3">
						<v-col cols="12">
							<v-select
								v-model="medicine_interaction_id"
								:items="drugInteractions"
								:label="$gettext('Drug Interaction')"
								item-text="name"
								item-value="id"
								outlined
								class="px-2"
							></v-select>
						</v-col>
						<v-col cols="12" v-if="$store.getters.hasRole('admin')">
							<v-row no-gutters>
								<v-col cols="6">
									<v-text-field
										v-model="no_effect_quantity"
										:label="$gettext('No effect quantity')"
										class="px-2"
										outlined
									/>
								</v-col>
								<v-col cols="6">
									<v-text-field
										v-model="minimum_effect_quantity"
										:label="$gettext('Minimum effect quantity')"
										class="px-2"
										outlined
									/>
								</v-col>
								<v-col cols="6">
									<v-text-field
										v-model="moderate_effect_quantity"
										:label="$gettext('Moderate effect quantity')"
										class="px-2"
										outlined
									/>
								</v-col>
								<v-col cols="6">
									<v-text-field
										v-model="high_effect_quantity"
										:label="$gettext('High effect quantity')"
										class="px-2"
										outlined
									/>
								</v-col>
							</v-row>
						</v-col>
						<v-col cols="12" v-if="$store.getters.hasRole('admin')">
							<v-text-field
								v-model="info_pt"
								:label="$gettext('Info') + ' PT'"
								class="px-2"
								outlined
							/>
							<v-text-field
								v-model="info_en"
								:label="$gettext('Info') + ' EN'"
								class="px-2"
								outlined
							/>
							<v-text-field
								v-model="info_es"
								:label="$gettext('Info') + ' ES'"
								class="px-2"
								outlined
							/>
						</v-col>
						<v-col cols="12" v-if="$store.getters.hasRole('admin')">
							<v-text-field
								v-model="endpoint_pt"
								:label="$gettext('Endpoint') + ' PT'"
								class="px-2"
								outlined
							/>
							<v-text-field
								v-model="endpoint_en"
								:label="$gettext('Endpoint') + ' EN'"
								class="px-2"
								outlined
							/>
							<v-text-field
								v-model="endpoint_es"
								:label="$gettext('Endpoint') + ' ES'"
								class="px-2"
								outlined
							/>
						</v-col>
						<v-col cols="12" v-if="$store.getters.hasRole('admin')">
							<v-text-field
								v-model="result_pt"
								:label="$gettext('Result') + ' PT'"
								class="px-2"
								outlined
							/>
							<v-text-field
								v-model="result_en"
								:label="$gettext('Result') + ' EN'"
								class="px-2"
								outlined
							/>
							<v-text-field
								v-model="result_es"
								:label="$gettext('Result') + ' ES'"
								class="px-2"
								outlined
							/>
						</v-col>
						<v-col cols="12" v-if="$store.getters.hasRole('admin')">
							<v-text-field
								v-model="recommendation_pt"
								:label="$gettext('Recommendation') + ' PT'"
								class="px-2"
								outlined
							/>
							<v-text-field
								v-model="recommendation_en"
								:label="$gettext('Recommendation') + ' EN'"
								class="px-2"
								outlined
							/>
							<v-text-field
								v-model="recommendation_es"
								:label="$gettext('Recommendation') + ' ES'"
								class="px-2"
								outlined
							/>
						</v-col>
						<v-col cols="12" v-if="$store.getters.hasRole('admin')">
							<SearchBibliographicReferences class="px-2" :selectedBibliographicReferences="bibliographicReferences" />
						</v-col>
					</v-row>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn color="primary darken-1" text @click="updateDrugs">
						<translate>Add</translate>
					</v-btn>
					<v-btn color="primary darken-1" text @click="showDrugInteractionOptions = false; drug = null; drugOptions = []">
						<translate>Cancel</translate>
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import axios from "axios";
import DrugList from "@/components/drugs/DrugList.vue";
import SearchBibliographicReferences from "@/components/bibliographic_references/SearchBibliographicReferences.vue";
import { mapGetters } from 'vuex'

export default {
	name: "SearchDrugs",
	props: ["selectedDrugs", "context", "drugInteractions", "disabled"],
	components: {
		DrugList,
		SearchBibliographicReferences
	},
	data: () => ({
		drug: null,
		drugOptions: [],
		search: "",
		loadingDrugs: false,
		showAppointmentsToImport: false,
		appointmentsToImport: [],
		showDrugInteractionOptions: false,
		info_pt: "",
		info_en: "",
		info_es: "",
		endpoint_pt: "",
		endpoint_en: "",
		endpoint_es: "",
		result_pt: "",
		result_en: "",
		result_es: "",
		recommendation_pt: "",
		recommendation_en: "",
		recommendation_es: "",
		no_effect_quantity: null,
		minimum_effect_quantity: null,
		moderate_effect_quantity: null,
		high_effect_quantity: null,
		medicine_interaction_id: null,
		bibliographicReferences: []
	}),
	methods: {
		clickDrug() {
			if(this.context == 'appointment') this.updateDrugs();
			else this.showDrugInteractionOptions = true;
		},
		makeRequest: function(value, self) {
			clearTimeout(this._timerId);
			self.loadingDrugs = true;

			this._timerId = setTimeout(() => {
				self.loadingDrugs = true;
				console.log("API CALL");
				axios
					.get("/app/drugssearch", {
						params: { value: value, page: 1 }
					})
					.then(response => this.addDrugs(response.data))
					.catch(error => console.log(error));
			}, 300);
		},
		addDrugs(drugs) {
			console.log(drugs);
			this.drugOptions = drugs;
			if (drugs.length != 0 && this.context == "new-product") {
				console.log('ADD ALL OPTION');
				this.drugOptions.unshift({ id: -1, listName: this.$gettext("Add all") });
			}
			this.loadingDrugs = false;
		},
		updateDrugs: function() {
			console.log("UPDATE");
			if (this.drug.id == -1) {
				this.drugOptions.splice(this.drugOptions.indexOf(this.drug), 1);
				this.drugOptions.forEach(d => {
					this.addDrug(d);
				});
			} else {
				this.addDrug(this.drug);
			}
			this.drug = null;
			this.search = null;
			this.drugOptions = [];
			this.showDrugInteractionOptions = false;
		},
		addDrug(drug) {
			if (drug) {
				console.log(this.drug);
				var exist = false;
				for (var i = 0; i < this.selectedDrugs.length; i++) {
					if (this.selectedDrugs[i].id == drug.id) {
						exist = true;
						this.selectedDrugs[i].interaction = {
							medicine_interaction_id: this.medicine_interaction_id,
							info_pt: this.info_pt,
							info_en: this.info_en,
							info_es: this.info_es,
							endpoint_pt: this.endpoint_pt,
							endpoint_en: this.endpoint_en,
							endpoint_es: this.endpoint_es,
							result_pt: this.result_pt,
							result_en: this.result_en,
							result_es: this.result_es,
							recommendation_pt: this.recommendation_pt,
							recommendation_en: this.recommendation_en,
							recommendation_es: this.recommendation_es,
							no_effect_quantity: this.no_effect_quantity,
							minimum_effect_quantity: this.minimum_effect_quantity,
							moderate_effect_quantity: this.moderate_effect_quantity,
							high_effect_quantity: this.high_effect_quantity,
							bibliographic_references: this.bibliographicReferences
						}
						break;
					}
				}
				if (!exist) {
					//CALL API
					console.log("ADD DRUG");
					if (this.context == "appointment") {
						const appointmentId = this.$route.params.aid;
						axios
							.post("/app/adddrug", null, {
								params: {
									appointment_id: appointmentId,
									drug_id: drug.id
								}
							})
							.then(response => {
								console.log(response.data);
								if (response.status === 200) {
									if (response.data.result === "added") {
										this.selectedDrugs.push(response.data.drug);
									}
								}
							})
							.catch(error => console.log(error));
					} else {
						drug.interaction = {
							medicine_interaction_id: this.medicine_interaction_id,
							info_pt: this.info_pt,
							info_en: this.info_en,
							info_es: this.info_es,
							endpoint_pt: this.endpoint_pt,
							endpoint_en: this.endpoint_en,
							endpoint_es: this.endpoint_es,
							result_pt: this.result_pt,
							result_en: this.result_en,
							result_es: this.result_es,
							recommendation_pt: this.recommendation_pt,
							recommendation_en: this.recommendation_en,
							recommendation_es: this.recommendation_es,
							no_effect_quantity: this.no_effect_quantity,
							minimum_effect_quantity: this.minimum_effect_quantity,
							moderate_effect_quantity: this.moderate_effect_quantity,
							high_effect_quantity: this.high_effect_quantity,
							bibliographic_references: this.bibliographicReferences
						}
						this.selectedDrugs.push(drug);
					}
				}
			}
		},
		removeDrug: function(drug) {
			//CALL API
			console.log("REMOVE DRUG");
			if (this.context == "appointment") {
				const appointmentId = this.$route.params.aid;
				axios
					.post("/app/removedrug", null, {
						params: {
							appointment_id: appointmentId,
							drug_id: drug.id
						}
					})
					.then(response => {
						console.log(response.data);
						if (response.status === 200) {
							if (response.data.result === "deleted") {
								this.selectedDrugs.splice(this.selectedDrugs.indexOf(drug), 1);
							}
						}
					})
					.catch(error => console.log(error));
			} else {
				this.selectedDrugs.splice(this.selectedDrugs.indexOf(drug), 1);
			}
		},
		editDrug: function(drug) {
			if (this.context == "appointment") {
				const appointmentId = this.$route.params.aid;
				axios
					.post("/app/edit_drug_info", null, {
						params: {
							appointment_id: appointmentId,
							drug_id: drug.id,
							info: drug.info
						}
					})
					.then(response => {
						console.log(response.data);
						if (response.status === 200) {
							if (response.data.result === "edited") {
								console.log("EDITED DRUG INFO" + drug.info);
							}
						}
					})
					.catch(error => console.log(error));
			}
		},
		importDrugs() {
			const patientId = this.$route.params.pid;
			const appointmentId = this.$route.params.aid;
			axios
				.get(
					"/patient/" +
						patientId +
						"/appointment/" +
						appointmentId +
						"/import_options"
				)
				.then(response => {
					console.log(response.data);
					if (response.status === 200) {
						this.appointmentsToImport = response.data.appointments;
						this.showAppointmentsToImport = true;
					}
				})
				.catch(error => console.log(error));
		},
		importAppointmentDrugs(id) {
			console.log("IMPORTING APPOINTMENT DRUGS");
			this.$emit("import-drugs", id);
			this.showAppointmentsToImport = false;
		}
	},
	computed: {
		...mapGetters({
      tourStep: 'tourStepEvent'
    })
	},
	watch: {
		search(value) {
			if (!value) {
				return;
			}
			//if(value == "") return;
			if(this.drug) return;
			console.log("CALL DEBOUNCE");
			this.drugOptions = [];
			this.makeRequest(value, this);
			//debounce(this.makeSearch, 200)(value, this);
		},
		tourStep(value) {
			if(value == 'tour-anamnesis-add-drug') {
				this.addDrug({id: 17520});
			}
		}
	}
};
</script>